import type { Exercice } from '@mairistem/comptabilite';
import { create } from 'zustand';
import TreeIndex from '../classes/pieces/TreeIndex';
import TreeNodeType from '../constants/liquidation/TreeNodeType';
import TreeRowKey from '../constants/liquidation/TreeRowKey';
import type SensOptions from '../interfaces/pieces/bordereaux/SensOptions';
import { SENS_TOUS } from '../interfaces/pieces/bordereaux/SensOptions';
import type { TreeRow } from '../interfaces/pieces/TreeRow';

/** Années d'exercice dont l'arbre est gardé en mémoire : au-delà, la moins récemment affichée est oubliée. */
export const MAX_ANNEES_EN_CACHE = 2;

/**
 * Nombre de pièces chargées au-delà duquel les pièces des bordereaux repliés les moins récemment ouverts sont
 * libérées (rechargées s'ils sont rouverts). Une pièce pèse environ 1,5 Ko de JSON (mesuré sur un bordereau de
 * 5 000 pièces).
 */
export const MAX_PIECES_EN_CACHE = 20_000;

/** Clé de chargement des entités (racines) d'une année. */
export const ROOT_LOADING_KEY = '';

/** Ce que la fiche a modifié et que l'arbre doit relire, par identifiant. */
export interface DemandesRafraichissement {
	/** Pièces créées ou modifiées. */
	pieces: ReadonlySet<number>;
	piecesSupprimees: ReadonlySet<number>;
	/** Bordereaux à relire (montant, nombre de pièces, état) ou créés depuis la fiche. */
	bordereaux: ReadonlySet<number>;
}

export interface ArbreAnnee {
	index: TreeIndex;
	isRacinesChargees: boolean;
	expandedKeys: ReadonlySet<string>;
	loadingKeys: ReadonlySet<string>;
	scrollTop: number;
	/** Bordereaux dont les pièces sont chargées, avec leur dernier accès : ordre de libération des pièces. */
	accesBordereaux: ReadonlyMap<string, number>;
	aRafraichir: DemandesRafraichissement;
	/**
	 * Dernière relecture en échec, ses demandes remises dans `aRafraichir` : pas de nouvel essai tant que l'arbre
	 * reste affiché, sauf nouvelle demande ; relancée au prochain affichage (relancerRafraichissements).
	 */
	isRafraichissementEnEchec: boolean;
}

interface LiquidationTreeviewState {
	/** Arbre par année d'exercice, de la moins récemment affichée à la plus récente. */
	annees: ReadonlyMap<number, ArbreAnnee>;
	anneeExercice: number | undefined;
	sens: SensOptions;
	etatBordereau: number | null;
	/** Années proposées dans le filtre, chargées une seule fois. */
	exercices: Partial<Exercice>[] | null;

	setAnneeExercice: (annee: number) => void;
	setSens: (sens: SensOptions) => void;
	setEtatBordereau: (etat: number | null) => void;
	setExercices: (exercices: Partial<Exercice>[]) => void;

	setRacines: (annee: number, index: TreeIndex) => void;
	/** Enfants chargés d'une ligne ; pour un bordereau, compte dans le plafond de pièces. */
	setEnfants: (annee: number, rowKey: string, enfants: TreeRow[]) => void;
	setLoading: (annee: number, rowKey: string, isLoading: boolean) => void;
	setExpanded: (annee: number, rowKey: string, isExpanded: boolean) => void;
	/** Déplie une pièce en repliant celle qui l'était : une seule pièce dépliée à la fois. */
	toggleMouvementDeplie: (annee: number, rowKey: string) => void;
	removeExpanded: (annee: number, rowKeys: string[]) => void;
	updateIndex: (annee: number, update: (index: TreeIndex) => TreeIndex) => void;
	setScrollTop: (annee: number, scrollTop: number) => void;

	/** Pièce créée ou enregistrée depuis la fiche (éventuellement changée de bordereau). */
	signalerPiece: (
		identifiant: number | string,
		identifiantBordereau?: number | string | null,
	) => void;
	signalerPieceSupprimee: (identifiant: number) => void;
	/** Bordereau créé ou modifié depuis la fiche. */
	signalerBordereau: (
		identifiant: number | string,
		identifiantExercice?: number,
	) => void;
	/** Demandes de rafraîchissement d'une année, retirées du store : l'appelant les traite. */
	prendreRafraichissements: (annee: number) => DemandesRafraichissement | null;
	/** Remet des demandes prises dont la relecture a échoué : elles seront relues au prochain affichage. */
	rendreRafraichissements: (
		annee: number,
		demandes: DemandesRafraichissement,
	) => void;
	/** Affichage de l'arbre : les demandes remises après un échec peuvent être relues. */
	relancerRafraichissements: (annee: number) => void;

	reset: () => void;
}

const AUCUNE_DEMANDE: DemandesRafraichissement = {
	pieces: new Set(),
	piecesSupprimees: new Set(),
	bordereaux: new Set(),
};

const hasDemandes = ({
	pieces,
	piecesSupprimees,
	bordereaux,
}: DemandesRafraichissement): boolean =>
	pieces.size > 0 || piecesSupprimees.size > 0 || bordereaux.size > 0;

/** Demandes réunies ; renvoie `a` elle-même si `b` n'ajoute rien. */
const fusionnerDemandes = (
	a: DemandesRafraichissement,
	b: DemandesRafraichissement,
): DemandesRafraichissement => {
	const pieces = withAdded(a.pieces, ...b.pieces);
	const piecesSupprimees = withAdded(a.piecesSupprimees, ...b.piecesSupprimees);
	const bordereaux = withAdded(a.bordereaux, ...b.bordereaux);
	return pieces === a.pieces &&
		piecesSupprimees === a.piecesSupprimees &&
		bordereaux === a.bordereaux
		? a
		: { pieces, piecesSupprimees, bordereaux };
};

const newArbre = (): ArbreAnnee => ({
	index: TreeIndex.EMPTY,
	isRacinesChargees: false,
	expandedKeys: new Set(),
	loadingKeys: new Set(),
	scrollTop: 0,
	accesBordereaux: new Map(),
	aRafraichir: AUCUNE_DEMANDE,
	isRafraichissementEnEchec: false,
});

const withAdded = <T>(set: ReadonlySet<T>, ...values: T[]): ReadonlySet<T> => {
	const added = values.filter((value) => !set.has(value));
	return added.length ? new Set([...set, ...added]) : set;
};

const withoutValues = <T>(set: ReadonlySet<T>, values: T[]): ReadonlySet<T> => {
	const removed = values.filter((value) => set.has(value));
	if (!removed.length) return set;
	const next = new Set(set);
	removed.forEach((value) => {
		next.delete(value);
	});
	return next;
};

/** Horloge des accès aux bordereaux : seul l'ordre compte. */
let dernierAcces = 0;

/**
 * Libère les pièces des bordereaux repliés les moins récemment ouverts tant que le plafond est dépassé, en
 * commençant par les années qui ne sont pas affichées. Un bordereau déplié, en cours de chargement ou qui vient
 * d'être chargé (`protege`) n'est jamais libéré.
 */
const libererPiecesExcedentaires = (
	annees: ReadonlyMap<number, ArbreAnnee>,
	anneeAffichee: number | undefined,
	protege: { annee: number; rowKey: string },
): ReadonlyMap<number, ArbreAnnee> => {
	let total = 0;
	const candidats: {
		annee: number;
		rowKey: string;
		acces: number;
		nb: number;
	}[] = [];

	annees.forEach((arbre, annee) => {
		arbre.accesBordereaux.forEach((acces, rowKey) => {
			const nb = arbre.index.getChildKeys(rowKey)?.length ?? 0;
			total += nb;
			const isProtege = annee === protege.annee && rowKey === protege.rowKey;
			if (
				!isProtege &&
				!arbre.expandedKeys.has(rowKey) &&
				!arbre.loadingKeys.has(rowKey)
			) {
				candidats.push({ annee, rowKey, acces, nb });
			}
		});
	});

	if (total <= MAX_PIECES_EN_CACHE) return annees;

	candidats.sort(
		(a, b) =>
			Number(a.annee === anneeAffichee) - Number(b.annee === anneeAffichee) ||
			a.acces - b.acces,
	);

	const aLiberer = new Map<number, string[]>();
	for (const candidat of candidats) {
		if (total <= MAX_PIECES_EN_CACHE) break;
		total -= candidat.nb;
		aLiberer.set(candidat.annee, [
			...(aLiberer.get(candidat.annee) ?? []),
			candidat.rowKey,
		]);
	}

	const next = new Map(annees);
	aLiberer.forEach((rowKeys, annee) => {
		const arbre = next.get(annee);
		const accesBordereaux = new Map(arbre.accesBordereaux);
		// les pièces dépliées des bordereaux libérés ne le seront plus au rechargement
		const clesPieces: string[] = [];
		// une seule copie de l'index pour tous les bordereaux libérés
		const index = arbre.index.edit((draft) => {
			rowKeys.forEach((rowKey) => {
				clesPieces.push(...(draft.getChildKeys(rowKey) ?? []));
				draft.unloadChildren(rowKey);
				accesBordereaux.delete(rowKey);
			});
		});
		next.set(annee, {
			...arbre,
			index,
			accesBordereaux,
			expandedKeys: withoutValues(arbre.expandedKeys, clesPieces),
		});
	});
	return next;
};

/**
 * État de la treeview d'ordonnancement conservé hors du composant : il survit à la navigation vers une fiche et au
 * retour (arbre chargé, lignes dépliées, défilement, filtres). En mémoire seulement, il est perdu au rechargement
 * de la page.
 */
const useLiquidationTreeviewStore = create<LiquidationTreeviewState>()(
	(set, get) => {
		/** Modifie l'arbre d'une année s'il est en cache (ignoré sinon : année oubliée entre-temps). */
		const updateArbre = (
			annee: number,
			update: (arbre: ArbreAnnee) => ArbreAnnee,
		): void =>
			set(({ annees }) => {
				const arbre = annees.get(annee);
				if (!arbre) return {};
				const nextArbre = update(arbre);
				if (nextArbre === arbre) return {};
				const next = new Map(annees);
				next.set(annee, nextArbre);
				return { annees: next };
			});

		/** Ajoute une demande de rafraîchissement aux années dont l'arbre est concerné. */
		const demanderRafraichissement = (
			isConcerne: (arbre: ArbreAnnee) => DemandesRafraichissement | null,
		): void =>
			set(({ annees }) => {
				let next: Map<number, ArbreAnnee> | null = null;
				annees.forEach((arbre, annee) => {
					const demandes = isConcerne(arbre);
					if (!demandes) return;
					next ??= new Map(annees);
					// une nouvelle demande relance aussi celles remises après un échec
					next.set(annee, {
						...arbre,
						aRafraichir: fusionnerDemandes(arbre.aRafraichir, demandes),
						isRafraichissementEnEchec: false,
					});
				});
				return next ? { annees: next } : {};
			});

		/** Identifiant du bordereau parent (dans l'arbre) d'une pièce, s'il y en a un. */
		const getIdentifiantBordereauParent = (
			arbre: ArbreAnnee,
			identifiantPiece: number,
		): number | undefined => {
			const parent = arbre.index.getParent(
				TreeRowKey.mouvement(identifiantPiece),
			);
			return parent?.nodeType === TreeNodeType.BORDEREAU
				? Number(parent.identifiant)
				: undefined;
		};

		return {
			annees: new Map(),
			anneeExercice: undefined,
			sens: SENS_TOUS,
			etatBordereau: null,
			exercices: null,

			setAnneeExercice: (annee) =>
				set(({ annees }) => {
					const next = new Map(annees);
					const arbre = next.get(annee) ?? newArbre();
					// réinsérée en fin : la plus récemment affichée
					next.delete(annee);
					next.set(annee, arbre);
					while (next.size > MAX_ANNEES_EN_CACHE) {
						next.delete(next.keys().next().value);
					}
					return { anneeExercice: annee, annees: next };
				}),
			setSens: (sens) => set({ sens }),
			setEtatBordereau: (etatBordereau) => set({ etatBordereau }),
			setExercices: (exercices) => set({ exercices }),

			setRacines: (annee, index) =>
				updateArbre(annee, (arbre) => ({
					...arbre,
					index,
					isRacinesChargees: true,
					accesBordereaux: new Map(),
				})),
			setEnfants: (annee, rowKey, enfants) => {
				updateArbre(annee, (arbre) => {
					const index = arbre.index.setChildren(rowKey, enfants);
					if (index === arbre.index) return arbre;
					const isBordereau =
						index.get(rowKey).nodeType === TreeNodeType.BORDEREAU;
					if (!isBordereau) return { ...arbre, index };
					dernierAcces += 1;
					return {
						...arbre,
						index,
						accesBordereaux: new Map(arbre.accesBordereaux).set(
							rowKey,
							dernierAcces,
						),
					};
				});
				set(({ annees, anneeExercice }) => {
					const next = libererPiecesExcedentaires(annees, anneeExercice, {
						annee,
						rowKey,
					});
					return next === annees ? {} : { annees: next };
				});
			},
			setLoading: (annee, rowKey, isLoading) =>
				updateArbre(annee, (arbre) => {
					const loadingKeys = isLoading
						? withAdded(arbre.loadingKeys, rowKey)
						: withoutValues(arbre.loadingKeys, [rowKey]);
					return loadingKeys === arbre.loadingKeys
						? arbre
						: { ...arbre, loadingKeys };
				}),
			setExpanded: (annee, rowKey, isExpanded) =>
				updateArbre(annee, (arbre) => {
					// replier un bordereau replie aussi sa pièce dépliée : elle ne le sera plus à sa réouverture
					const isBordereau =
						arbre.index.get(rowKey)?.nodeType === TreeNodeType.BORDEREAU;
					const expandedKeys = isExpanded
						? withAdded(arbre.expandedKeys, rowKey)
						: withoutValues(arbre.expandedKeys, [
								rowKey,
								...(isBordereau
									? (arbre.index.getChildKeys(rowKey) ?? [])
									: []),
							]);
					if (expandedKeys === arbre.expandedKeys) return arbre;
					if (!isExpanded || !arbre.accesBordereaux.has(rowKey)) {
						return { ...arbre, expandedKeys };
					}
					dernierAcces += 1;
					return {
						...arbre,
						expandedKeys,
						accesBordereaux: new Map(arbre.accesBordereaux).set(
							rowKey,
							dernierAcces,
						),
					};
				}),
			toggleMouvementDeplie: (annee, rowKey) =>
				updateArbre(annee, (arbre) => {
					const expandedKeys = new Set<string>();
					let isDejaDeplie = false;
					arbre.expandedKeys.forEach((key) => {
						if (arbre.index.get(key)?.nodeType === TreeNodeType.MOUVEMENT) {
							isDejaDeplie ||= key === rowKey;
							return;
						}
						expandedKeys.add(key);
					});
					if (!isDejaDeplie) {
						expandedKeys.add(rowKey);
					}
					return { ...arbre, expandedKeys };
				}),
			removeExpanded: (annee, rowKeys) =>
				updateArbre(annee, (arbre) => {
					const expandedKeys = withoutValues(arbre.expandedKeys, rowKeys);
					return expandedKeys === arbre.expandedKeys
						? arbre
						: { ...arbre, expandedKeys };
				}),
			updateIndex: (annee, update) =>
				updateArbre(annee, (arbre) => {
					const index = update(arbre.index);
					if (index === arbre.index) return arbre;
					// bordereaux supprimés ou dont les pièces ont été libérées : plus rien à compter
					let accesBordereaux = arbre.accesBordereaux;
					arbre.accesBordereaux.forEach((_acces, rowKey) => {
						if (!index.hasLoadedChildren(rowKey)) {
							if (accesBordereaux === arbre.accesBordereaux) {
								accesBordereaux = new Map(arbre.accesBordereaux);
							}
							(accesBordereaux as Map<string, number>).delete(rowKey);
						}
					});
					return { ...arbre, index, accesBordereaux };
				}),
			setScrollTop: (annee, scrollTop) =>
				updateArbre(annee, (arbre) =>
					arbre.scrollTop === scrollTop ? arbre : { ...arbre, scrollTop },
				),

			signalerPiece: (identifiantRecu, identifiantBordereauRecu) =>
				demanderRafraichissement((arbre) => {
					// la fiche peut transmettre les identifiants en texte : comparés en nombres partout
					const identifiant = Number(identifiantRecu);
					const identifiantBordereau =
						identifiantBordereauRecu === undefined ||
						identifiantBordereauRecu === null
							? undefined
							: Number(identifiantBordereauRecu);
					const isPieceDansArbre = arbre.index.has(
						TreeRowKey.mouvement(identifiant),
					);
					const isBordereauDansArbre =
						identifiantBordereau !== undefined &&
						arbre.index.has(TreeRowKey.bordereau(identifiantBordereau));
					if (!isPieceDansArbre && !isBordereauDansArbre) return null;
					// le bordereau qu'elle quitte et celui qu'elle rejoint changent de montant et de nombre de pièces
					const bordereaux = [
						getIdentifiantBordereauParent(arbre, identifiant),
						isBordereauDansArbre ? identifiantBordereau : undefined,
					].filter((id) => id !== undefined);
					return {
						pieces: new Set([identifiant]),
						piecesSupprimees: new Set(),
						bordereaux: new Set(bordereaux),
					};
				}),
			signalerPieceSupprimee: (identifiant) =>
				demanderRafraichissement((arbre) => {
					if (!arbre.index.has(TreeRowKey.mouvement(identifiant))) return null;
					const identifiantBordereau = getIdentifiantBordereauParent(
						arbre,
						identifiant,
					);
					return {
						pieces: new Set(),
						piecesSupprimees: new Set([identifiant]),
						bordereaux: new Set(
							identifiantBordereau === undefined ? [] : [identifiantBordereau],
						),
					};
				}),
			signalerBordereau: (identifiantRecu, identifiantExercice) =>
				demanderRafraichissement((arbre) => {
					const identifiant = Number(identifiantRecu);
					const isConcerne =
						arbre.index.has(TreeRowKey.bordereau(identifiant)) ||
						arbre.index
							.getRoots()
							.some(
								(entite) =>
									entite.exercice?.identifiant === identifiantExercice &&
									arbre.index.hasLoadedChildren(entite.rowKey),
							);
					return isConcerne
						? {
								pieces: new Set(),
								piecesSupprimees: new Set(),
								bordereaux: new Set([identifiant]),
							}
						: null;
				}),
			prendreRafraichissements: (annee) => {
				const arbre = get().annees.get(annee);
				if (!arbre || !hasDemandes(arbre.aRafraichir)) return null;
				updateArbre(annee, (courant) => ({
					...courant,
					aRafraichir: AUCUNE_DEMANDE,
				}));
				return arbre.aRafraichir;
			},
			rendreRafraichissements: (annee, demandes) =>
				updateArbre(annee, (arbre) => ({
					...arbre,
					aRafraichir: fusionnerDemandes(arbre.aRafraichir, demandes),
					isRafraichissementEnEchec: true,
				})),
			relancerRafraichissements: (annee) =>
				updateArbre(annee, (arbre) =>
					arbre.isRafraichissementEnEchec
						? { ...arbre, isRafraichissementEnEchec: false }
						: arbre,
				),

			reset: () =>
				set({
					annees: new Map(),
					anneeExercice: undefined,
					sens: SENS_TOUS,
					etatBordereau: null,
					exercices: null,
				}),
		};
	},
);

export default useLiquidationTreeviewStore;
